import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  Menu,
  ListItemIcon,
  ListItemText,
  Alert,
  Snackbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DataGrid, itIT } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FilterListIcon from '@mui/icons-material/FilterList';
import SpeedIcon from '@mui/icons-material/Speed';
import { metersApi, plantsApi, customersApi } from '../../services/api';
import ResponsiveHeader from '../../components/ResponsiveHeader';
import ResponsiveFilters from '../../components/ResponsiveFilters';
import MobileCardList from '../../components/MobileCardList';

const MeterList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Stato filtri
  const [filters, setFilters] = useState({
    plant_id: '',
    citta: '',
    provincia: ''
  });
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Stato paginazione DataGrid
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25
  });

  // Stato ordinamento
  const [sortModel, setSortModel] = useState([{ field: 'sotto_zona', sort: 'asc' }]);

  // Stato menu contestuale
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedMeter, setSelectedMeter] = useState(null);

  // Dialog per cambio cliente
  const [changeCustomerOpen, setChangeCustomerOpen] = useState(false);
  const [newCustomer, setNewCustomer] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Debounce ricerca
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Query contatori
  const { data: metersData, isLoading: loadingMeters, error: metersError } = useQuery({
    queryKey: ['meters', filters, debouncedSearch, paginationModel, sortModel],
    queryFn: async () => {
      const params = {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        ...(filters.plant_id && { plant_id: filters.plant_id }),
        ...(filters.citta && { citta: filters.citta }),
        ...(filters.provincia && { provincia: filters.provincia }),
        ...(debouncedSearch && { search: debouncedSearch })
      };
      if (sortModel.length > 0) {
        params.sortBy = sortModel[0].field;
        params.sortOrder = sortModel[0].sort;
      }
      const response = await metersApi.getAll(params);
      return response.data;
    }
  });

  // Query impianti per filtro
  const { data: plantsData } = useQuery({
    queryKey: ['plants-list'],
    queryFn: async () => {
      const response = await plantsApi.getAll({ limit: 1000 });
      return response.data;
    }
  });

  // Query clienti per autocomplete
  const { data: customersData } = useQuery({
    queryKey: ['customers-list'],
    queryFn: async () => {
      const response = await customersApi.getAll({ limit: 1000 });
      return response.data;
    }
  });

  // Query opzioni filtro (città/provincia)
  const { data: filterOptionsData } = useQuery({
    queryKey: ['meters-filter-options'],
    queryFn: async () => {
      const response = await metersApi.getFilterOptions();
      return response.data;
    },
    staleTime: 5 * 60 * 1000 // Cache per 5 minuti
  });

  const allCitta = React.useMemo(() => filterOptionsData?.citta || [], [filterOptionsData?.citta]);
  const cittaByProvincia = React.useMemo(() => filterOptionsData?.cittaByProvincia || {}, [filterOptionsData?.cittaByProvincia]);
  
  const filterOptions = {
    citta: allCitta,
    provincia: filterOptionsData?.provincia || []
  };
  
  // Filtra le città in base alla provincia selezionata
  const filteredCitta = React.useMemo(() => {
    return filters.provincia 
      ? (cittaByProvincia[filters.provincia.toLowerCase()] || [])
      : allCitta;
  }, [filters.provincia, cittaByProvincia, allCitta]);

  // Mutation per aggiornare contatore
  const updateMeterMutation = useMutation({
    mutationFn: ({ id, data }) => metersApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['meters']);
      setSnackbar({ open: true, message: 'Cliente aggiornato con successo', severity: 'success' });
      setChangeCustomerOpen(false);
      setNewCustomer(null);
    },
    onError: (error) => {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Errore durante l\'aggiornamento', 
        severity: 'error' 
      });
    }
  });

  // Handler menu contestuale
  const handleContextMenu = useCallback((event, meter) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedMeter(meter);
    setContextMenu({ mouseX: event.clientX, mouseY: event.clientY });
  }, []);

  const handleCloseMenu = () => {
    setContextMenu(null);
  };

  const handleViewMeter = () => {
    if (selectedMeter) {
      navigate(`/crm/contatori/${selectedMeter.id}`);
    }
    handleCloseMenu();
  };

  const handleEditMeter = () => {
    if (selectedMeter) {
      navigate(`/crm/contatori/${selectedMeter.id}/modifica`);
    }
    handleCloseMenu();
  };

  const handleChangeCustomer = () => {
    setChangeCustomerOpen(true);
    handleCloseMenu();
  };

  const handleConfirmChangeCustomer = () => {
    if (selectedMeter && newCustomer) {
      updateMeterMutation.mutate({
        id: selectedMeter.id,
        data: { customer_id: newCustomer.id }
      });
    }
  };

  // Reset filtri
  const handleResetFilters = () => {
    setFilters({ plant_id: '', citta: '', provincia: '' });
    setSearchText('');
  };

  // Colonne DataGrid
  const columns = [
    {
      field: 'sotto_zona',
      headerName: 'Progr.',
      width: 80,
      valueGetter: (params) => params.row.sotto_zona || ''
    },
    {
      field: 'meter_number',
      headerName: 'Matricola',
      width: 130,
      valueGetter: (params) => params.row.meter_number || params.row.nu_conta || params.row.c_cod_cont || '-'
    },
    {
      field: 'c_nome',
      headerName: 'Intestatario',
      width: 200,
      flex: 1
    },
    {
      field: 'c_localita',
      headerName: 'Località',
      width: 180
    },
    {
      field: 'c_citta',
      headerName: 'Città',
      width: 130
    },
    {
      field: 'c_provinci',
      headerName: 'Prov.',
      width: 70
    },
    {
      field: 'plant_name',
      headerName: 'Impianto',
      width: 150,
      valueGetter: (params) => params.row.plant?.nome || params.row.plant?.codice || '-'
    },
    {
      field: 'azienda',
      headerName: 'Azienda',
      width: 150,
      valueGetter: (params) => params.row.plant?.azienda?.company_name || '-',
    },
    {
      field: 'ultima_lettura',
      headerName: 'Ultima Lettura',
      width: 120,
      type: 'number',
      valueGetter: (params) => params.row.ultima_lettura || params.row.c_ult_lett || null,
      valueFormatter: (params) => params.value ? `${params.value} mc` : '-'
    },
    {
      field: 'data_ultima_lettura',
      headerName: 'Data Lettura',
      width: 120,
      valueGetter: (params) => params.row.data_ultima_lettura || params.row.c_ult_data || null,
      valueFormatter: (params) => {
        if (!params.value) return '-';
        const date = new Date(params.value);
        return date.toLocaleDateString('it-IT');
      }
    },
    {
      field: 'stato',
      headerName: 'Stato',
      width: 100,
      valueGetter: (params) => params.row.stato || params.row.c_stato || 'attivo',
      renderCell: (params) => {
        const stato = params.row.stato || params.row.c_stato || 'attivo';
        const color = stato === 'attivo' ? 'success' : stato === 'sospeso' ? 'warning' : 'error';
        return <Chip label={stato} color={color} size="small" />;
      }
    },
    {
      field: 'actions',
      headerName: 'Azioni',
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => handleContextMenu(e, params.row)}
        >
          <MoreVertIcon />
        </IconButton>
      )
    }
  ];

  const meters = metersData?.data?.meters || metersData?.data || [];
  const totalCount = metersData?.data?.pagination?.total || metersData?.pagination?.total || meters.length;
  const allPlants = plantsData?.data?.plants || plantsData?.data || [];
  const customers = customersData?.data?.customers || customersData?.data || [];

  // Filtra gli impianti in base alla provincia selezionata
  const plants = filters.provincia 
    ? allPlants.filter(p => p.provincia?.toLowerCase() === filters.provincia.toLowerCase())
    : allPlants;

  // Reset plant_id se l'impianto selezionato non è più nella lista filtrata
  React.useEffect(() => {
    if (filters.plant_id && filters.provincia) {
      const plantExists = plants.some(p => p.id === filters.plant_id);
      if (!plantExists) {
        setFilters(prev => ({ ...prev, plant_id: '' }));
      }
    }
  }, [filters.provincia, filters.plant_id, plants]);

  // Reset citta se la città selezionata non è più nella lista filtrata per provincia
  React.useEffect(() => {
    if (filters.citta && filters.provincia) {
      const cittaExists = filteredCitta.includes(filters.citta);
      if (!cittaExists) {
        setFilters(prev => ({ ...prev, citta: '' }));
      }
    }
  }, [filters.provincia, filters.citta, filteredCitta]);

  // Prepara righe per mobile card
  const meterRows = meters.map(m => ({
    ...m,
    meterDisplay: m.meter_number || m.nu_conta || m.c_cod_cont || '-',
    plantName: m.plant?.nome || m.plant?.codice || '-',
    lastReading: m.ultima_lettura || m.c_ult_lett || null,
    readingDate: m.data_ultima_lettura || m.c_ult_data || null,
    statusValue: m.stato || m.c_stato || 'attivo',
  }));

  const activeFilterCount = (filters.plant_id ? 1 : 0) + (filters.citta ? 1 : 0) + (filters.provincia ? 1 : 0);

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <ResponsiveHeader
        title="Contatori"
        subtitle={`${totalCount} contatori`}
        primaryAction={{
          label: isMobile ? 'Nuovo' : 'Nuovo Contatore',
          onClick: () => navigate('/crm/contatori/nuovo'),
        }}
      />

      {/* Filtri e Ricerca */}
      <Card sx={{ mb: isMobile ? 1 : 3 }}>
        <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
          <ResponsiveFilters
            searchValue={searchText}
            onSearchChange={setSearchText}
            searchPlaceholder="Cerca matricola, intestatario..."
            activeFilterCount={activeFilterCount}
            onClearFilters={handleResetFilters}
          >
            <FormControl sx={{ minWidth: { xs: '100%', sm: 150 } }} size="small">
              <InputLabel>Impianto</InputLabel>
              <Select
                value={filters.plant_id}
                label="Impianto"
                onChange={(e) => setFilters({ ...filters, plant_id: e.target.value })}
              >
                <MenuItem value="">Tutti</MenuItem>
                {plants.map((plant) => (
                  <MenuItem key={plant.id} value={plant.id}>
                    {plant.codice || plant.plant_code} - {plant.citta || ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: { xs: '100%', sm: 120 } }} size="small">
              <InputLabel>Città</InputLabel>
              <Select
                value={filters.citta}
                label="Città"
                onChange={(e) => setFilters({ ...filters, citta: e.target.value })}
              >
                <MenuItem value="">Tutte</MenuItem>
                {filteredCitta.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: { xs: '100%', sm: 120 } }} size="small">
              <InputLabel>Provincia</InputLabel>
              <Select
                value={filters.provincia}
                label="Provincia"
                onChange={(e) => setFilters({ ...filters, provincia: e.target.value })}
              >
                <MenuItem value="">Tutte</MenuItem>
                {filterOptions.provincia.map((p) => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </ResponsiveFilters>
        </CardContent>
      </Card>

      {/* Errore */}
      {metersError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Errore nel caricamento dei contatori: {metersError.message}
        </Alert>
      )}

      {/* Lista/DataGrid */}
      {isMobile ? (
        <MobileCardList
          rows={meterRows}
          loading={loadingMeters}
          onRowClick={(row) => navigate(`/crm/contatori/${row.id}`)}
          emptyMessage="Nessun contatore trovato"
          pagination={{
            page: paginationModel.page,
            pageSize: paginationModel.pageSize,
            total: totalCount,
            onPageChange: (p) => setPaginationModel(prev => ({ ...prev, page: p })),
            onPageSizeChange: (ps) => setPaginationModel(prev => ({ ...prev, pageSize: ps })),
          }}
          renderCard={(m) => (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                  <Typography variant="subtitle2" noWrap sx={{ flex: 1, mr: 1 }}>
                    {m.c_nome || '-'}
                  </Typography>
                  <Chip
                    label={m.statusValue}
                    color={m.statusValue === 'attivo' ? 'success' : m.statusValue === 'sospeso' ? 'warning' : 'error'}
                    size="small"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 0.3 }}>
                  <Typography variant="caption" color="text.secondary">Mat: {m.meterDisplay}</Typography>
                  {m.sotto_zona && <Typography variant="caption" color="text.secondary">| Pr. {m.sotto_zona}</Typography>}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {m.c_citta && <Typography variant="caption" color="text.secondary">{m.c_citta}</Typography>}
                  {m.lastReading && <Typography variant="caption" color="primary.main">Ult: {m.lastReading} mc</Typography>}
                </Box>
              </Box>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleContextMenu(e, m); }}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        />
      ) : (
        <Paper sx={{ width: '100%' }}>
          <DataGrid
            rows={meters}
            columns={columns}
            loading={loadingMeters}
            paginationMode="server"
            rowCount={totalCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            autoHeight
            localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
            onRowClick={(params) => navigate(`/crm/contatori/${params.row.id}`)}
            sx={{
              border: 'none',
              '& .MuiDataGrid-row:hover': {
                cursor: 'pointer'
              }
            }}
          />
        </Paper>
      )}

      {/* Menu Contestuale */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleViewMeter}>
          <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Visualizza</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleEditMeter}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Modifica</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleChangeCustomer}>
          <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Cambia Cliente</ListItemText>
        </MenuItem>
      </Menu>

      {/* Dialog Cambio Cliente */}
      <Dialog 
        open={changeCustomerOpen} 
        onClose={() => setChangeCustomerOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Cambia Cliente Associato
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Contatore: <strong>{selectedMeter?.c_matricola}</strong> - {selectedMeter?.c_nome}
            </Typography>
            <Autocomplete
              sx={{ mt: 2 }}
              options={customers}
              value={newCustomer}
              onChange={(_, value) => setNewCustomer(value)}
              getOptionLabel={(option) => 
                `${option.c_ragsoc || option.c_nome || ''} ${option.c_cognome || ''} - ${option.c_codice || option.id}`
              }
              renderInput={(params) => (
                <TextField {...params} label="Seleziona nuovo cliente" fullWidth />
              )}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangeCustomerOpen(false)}>
            Annulla
          </Button>
          <Button 
            onClick={handleConfirmChangeCustomer}
            variant="contained"
            disabled={!newCustomer || updateMeterMutation.isLoading}
          >
            Conferma
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MeterList;
